import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X, Loader2 } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { VehicleCard } from "@/components/VehicleCard";
import { api, CATEGORIES, TRANSMISSIONS, FUELS } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const FilterSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">{label}</label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="h-10"><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export default function Cars() {
  const [params, setParams] = useSearchParams();
  const [cities, setCities] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  const [city, setCity] = useState(params.get("city") || "Toutes");
  const [category, setCategory] = useState(params.get("category") || "Tous");
  const [transmission, setTransmission] = useState("Toutes");
  const [fuel, setFuel] = useState("Tous");
  const [maxPrice, setMaxPrice] = useState("");
  const [start, setStart] = useState(params.get("start") || "");
  const [end, setEnd] = useState(params.get("end") || "");

  useEffect(() => {
    api.get("/cities").then((r) => setCities(["Toutes", ...r.data])).catch(() => {});
  }, []);

  const fetchVehicles = useCallback(() => {
    setLoading(true);
    const q = {};
    if (city && city !== "Toutes") q.city = city;
    if (category && category !== "Tous") q.category = category;
    if (transmission && transmission !== "Toutes") q.transmission = transmission;
    if (fuel && fuel !== "Tous") q.fuel = fuel;
    if (maxPrice) q.max_price = maxPrice;
    if (start && end) { q.start = start; q.end = end; }
    api.get("/vehicles", { params: q })
      .then((r) => setVehicles(r.data))
      .finally(() => setLoading(false));
  }, [city, category, transmission, fuel, maxPrice, start, end]);

  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);

  const reset = () => {
    setCity("Toutes"); setCategory("Tous"); setTransmission("Toutes"); setFuel("Tous"); setMaxPrice(""); setStart(""); setEnd("");
    setParams({});
  };

  return (
    <PublicLayout>
      <div className="lr-gradient-dark py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Explorer les véhicules</h1>
          <p className="mt-2 text-slate-300">Comparez les véhicules, les prix et les conditions des loueurs indépendants.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid lg:grid-cols-4 gap-8">
          <aside className="lg:col-span-1">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 sticky top-20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="flex items-center gap-2 font-semibold text-slate-900">
                  <SlidersHorizontal className="h-4 w-4" /> Filtres
                </h3>
                <button onClick={reset} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="filters-reset-btn">
                  <X className="h-3 w-3" /> Réinitialiser
                </button>
              </div>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Départ</label>
                    <input type="date" data-testid="filter-start-date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Retour</label>
                    <input type="date" data-testid="filter-end-date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" />
                  </div>
                </div>
                <FilterSelect label="Ville" value={city} onChange={setCity} options={cities} testid="filter-city" />
                <FilterSelect label="Catégorie" value={category} onChange={setCategory} options={CATEGORIES} testid="filter-category" />
                <FilterSelect label="Boîte" value={transmission} onChange={setTransmission} options={TRANSMISSIONS} testid="filter-transmission" />
                <FilterSelect label="Carburant" value={fuel} onChange={setFuel} options={FUELS} testid="filter-fuel" />
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5 block">Prix max (MAD/jour)</label>
                  <input
                    type="number"
                    value={maxPrice}
                    data-testid="filter-max-price"
                    onChange={(e) => setMaxPrice(e.target.value)}
                    placeholder="ex : 800"
                    className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  />
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-3">
            <p className="text-sm text-slate-500 mb-5" data-testid="results-count">
              {loading ? "Recherche..." : `${vehicles.length} véhicule(s) trouvé(s)`}
              {!loading && start && end && <span data-testid="results-dates"> · disponibles du {start} au {end}</span>}
            </p>
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>
            ) : vehicles.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-300 py-20 text-center text-slate-500">
                Aucun véhicule ne correspond à vos critères.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {vehicles.map((v) => <VehicleCard key={v.id} vehicle={v} dates={start && end ? { start, end } : null} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
